import React from "react"
import { Box, Text, Heading } from "rebass"

const ExcerptContent = () => {
  return (
    <Box>
      <Heading
        fontFamily="body"
        fontWeight="400"
        textAlign="center"
        lineHeight={1.45}
      >
        An Excerpt from Uncanny Valley <br /> INCENTIVES
      </Heading>
      <Text variant="excerpt">
        Depending on whom you ask, it was either the apex, the inflection point,
        or the beginning of the end for Silicon Valley’s startup scene—what
        cynics called a bubble, optimists called the future, and my future
        coworkers, high on the fumes of world-historical potential, breathlessly
        called the ecosystem. A social network everyone said they hated but no
        one could stop logging into went public at a valuation of
        one-hundred-odd billion dollars, its grinning founder ringing the
        closing bell over video chat, a death knell for affordable rent in San
        Francisco. Two hundred million people signed onto a microblogging
        platform that helped them feel close to celebrities and other strangers
        they’d loathe in real life. Artificial intelligence and virtual reality
        were coming into vogue, again. Self-driving cars were considered
        inevitable. Everything was moving to mobile. Everything was up in the
        cloud. The cloud was someone else’s computer, in an unmarked data center
        in the middle of Texas or Cork or Bavaria, but nobody cared. Everyone
        trusted it anyway.
      </Text>
      <Text variant="excerpt">
        It was a year of new optimism: the optimism of no hurdles, no limits, no
        bad ideas. The optimism of capital, power, and opportunity. Wherever
        money changed hands, enterprising technologists and MBAs were bound to
        follow. The word “disruption” proliferated, and everything was ripe for
        or vulnerable to it: sheet music, tuxedo rentals, home cooking, home
        buying, wedding planning, banking, shaving, credit lines, dry cleaning,
        the rhythm method. A website that allowed people to rent out their
        unused driveways raised four million dollars from elite firms on Sand
        Hill Road. A website taking on the kennel market—a petsitting and
        dog-walking app that disrupted neighborhood twelve-year-olds—raised
        fifty million. An app for coupon-clipping enabled an untold number of
        bored and curious urbanites to pay for services they never knew they
        needed, and for a while people were mainlining antiwrinkle toxins,
        taking trapeze lessons, and bleaching their assholes, just because they
        could do it at a discount.
      </Text>
      <Text variant="excerpt">
        It was the dawn of the era of the unicorns: startups valued, by their
        investors, at over a billion dollars. A prominent venture capitalist
        declared in the op-ed pages of an international business newspaper that
        software was eating the world, a claim that was subsequently cited in
        countless pitch decks and press releases and job listings as if it were
        proof of something—as if it were not just a clumsy and unpoetic
        metaphor, but evidence.
      </Text>
      <Text variant="excerpt">
        Outside of Silicon Valley, there seemed to be an overall resistance to
        taking any of it too seriously. There was a prevailing sentiment
        that—just like the last bubble—this, too, would eventually pass.
        Meanwhile, the industry expanded beyond the province of futurists and
        hardware enthusiasts, and settled into its new role as the scaffolding
        of everyday life. Not that I was aware of any of this—not that I was
        paying any attention at all. I didn’t even have apps on my phone. I had
        just turned twenty-five and was living on the edge of Brooklyn with a
        roommate I hardly knew, in an apartment filled with so much secondhand
        furniture it almost had a connection to history. I had a fragile but
        agreeable life: a job, as an assistant at a small literary agency in
        Manhattan; a smattering of beloved friends on whom I exercised my social
        anxiety, primarily by avoiding them.
      </Text>
      <Text variant="excerpt">
        But the corners seemed to be coming up. The wheels were coming off. I
        thought, every day, about applying to graduate school. My job was
        running its course. There was no room to grow, and after three years the
        voyeuristic thrill of answering someone else’s phone had worn thin. I no
        longer wanted to amuse myself with submissions from the slush pile, or
        continue filing author contracts and royalty statements in places where
        they did not belong, like my desk drawer. My freelance work,
        proofreading and copyediting manuscripts for a small press, was also
        waning in volume, because I had recently broken up with the editor who
        assigned it to me. The relationship had been fraught, but reliably
        consuming: the editor, several years my senior, had wanted to get
        married but wouldn’t stop cheating on me. These infidelities were
        revealed after he borrowed my laptop for a weekend and returned it
        without logging out of his accounts, where I read a series of romantic
        and brooding private messages he exchanged with a voluptuous folk singer
        via the social network everyone hated. That year, I hated it extra.
      </Text>
      <Text variant="excerpt">
        I was oblivious to Silicon Valley, and contentedly so. It’s not that I
        was a Luddite—I could point-and-click before I could read. I just never
        opened the business section. Like anyone else with a desk job, I spent
        the majority of my waking hours peering into a computer, typing and
        tabbing through the days, the web browser a current of digital
        digression running beneath my work. At home, I wasted time scrolling
        through the photos and errant musings of people I should have long since
        forgotten, and exchanged long, searching emails with friends, in which
        we swapped unaccredited professional and dating advice. I read the
        online archives of literary magazines that no longer existed, digitally
        window-shopped for clothing I could not afford, and created and
        abandoned private, aspirational blogs with names like A Meaningful Life,
        in the vain hope that they might push me closer to leading one. Still,
        it never occurred to me that I might someday become one of the people
        working behind the internet, because I had never considered that there
        were people behind the internet at all.
      </Text>
    </Box>
  )
}

export default ExcerptContent
